import { Plugin, ButtonView, ContextualBalloon, ClickObserver, clickOutsideHandler } from 'ckeditor5';
import { v4 as uuid } from 'uuid';
import { ConditionFormView } from './ConditionFormView';
import { ConditionState } from './State';
import { InsertConditionCommand } from './insertCommand';
import { ConditionsSchema } from './schema';
export class ConditionsUI extends Plugin {
    static get pluginName() {
        return 'ConditionsUI';
    }
    init() {
        const { editor } = this;
        this.insertCommand = editor.commands.get(InsertConditionCommand.pluginName);
        this.balloon = editor.plugins.get(ContextualBalloon);
        this.formView = this.createFormView();
        this.state = editor.plugins.get(ConditionState);
        editor.editing.view.addObserver(ClickObserver);
        this.listenTo(editor.editing.view.document, 'click', (evt, data) => {
            const domTarget = data.domEvent.target;
            const labelEl = domTarget.closest('.condition-label');
            if (!labelEl)
                return;
            const markerName = labelEl.getAttribute('data-marker-name');
            if (!markerName)
                return;
            if (!editor.model.markers.get(markerName))
                return;
            this.state.activeCondition = markerName;
            this.showEditForm(labelEl);
            evt.stop();
        }, { priority: 'high' });
        editor.ui.componentFactory.add('insertCondition', () => {
            const button = new ButtonView();
            button.bind('isEnabled').to(this.insertCommand);
            button.set({
                withText: true,
                label: 'Create Condition',
                class: 'pleditor-toolbar-fa-icon-button',
                tooltip: 'Add conditional marker to selected text',
            });
            button.on('execute', () => {
                this.insertCommand.addCondition({
                    id: uuid(),
                    type: 'assume',
                });
            });
            return button;
        });
        editor.ui.componentFactory.add('editCondition', () => {
            const button = new ButtonView();
            button.set({
                withText: true,
                label: 'Edit Condition',
                class: 'pleditor-toolbar-fa-icon-button',
                tooltip: 'Edit the condition at cursor position',
                isEnabled: false,
            });
            this.listenTo(editor.model.document.selection, 'change:range', () => {
                const markers = this.getMarkersAtSelection();
                button.isEnabled = markers.length > 0;
            });
            button.on('execute', () => {
                const marker = this.getMarkersAtSelection()[0];
                if (!marker)
                    return;
                this.state.activeCondition = marker.name;
                this.showEditForm();
            });
            return button;
        });
    }
    createFormView() {
        const { editor } = this;
        const formView = new ConditionFormView(editor.locale);
        this.listenTo(formView, 'submit', () => {
            const { conditionId, conditionType } = formView;
            this.updateCondition(conditionId, conditionType);
            this.hideEditForm();
        });
        this.listenTo(formView, 'cancel', () => {
            this.hideEditForm();
        });
        this.listenTo(formView, 'delete', () => {
            const marker = editor.model.markers.get(this.state.activeCondition);
            if (!marker)
                return;
            editor.model.change((writer) => {
                writer.removeMarker(marker.name);
            });
            this.hideEditForm();
        });
        clickOutsideHandler({
            emitter: formView,
            activator: () => this.balloon.visibleView === formView,
            contextElements: [this.balloon.view.element],
            callback: () => this.hideEditForm(),
        });
        return formView;
    }
    showEditForm(labelElement) {
        const { type, id } = this.state.getActiveCondition();
        if (!id) {
            return;
        }
        const position = labelElement ? { target: labelElement } : this.getBalloonPositionData();
        if (this.balloon.hasView(this.formView)) {
            this.balloon.remove(this.formView);
        }
        this.balloon.add({
            view: this.formView,
            position,
        });
        this.formView.conditionId = id;
        this.formView.conditionType = type;
        this.formView.focus();
    }
    hideEditForm() {
        this.state.unsetActiveCondition();
        if (this.balloon.hasView(this.formView)) {
            this.balloon.remove(this.formView);
        }
        this.editor.editing.view.focus();
    }
    updateCondition(newId, newType) {
        const { editor } = this;
        const marker = editor.model.markers.get(this.state.activeCondition);
        if (!marker)
            return;
        const parts = marker.name.split(':');
        const [markerType, , , instanceId] = parts;
        editor.model.change((writer) => {
            const range = marker.getRange();
            const newMarkerName = `${markerType}:${newType}:${newId}:${instanceId || uuid()}`;
            writer.removeMarker(marker.name);
            writer.addMarker(newMarkerName, { range, usingOperation: true, affectsData: true });
        });
        const editing = editor.plugins.get(ConditionsSchema);
        editing.state.addCondition(`${newType}:${newId}`);
    }
    getBalloonPositionData() {
        const { editor } = this;
        const modelRange = editor.model.document.selection.getFirstRange();
        if (!modelRange) {
            return {};
        }
        const viewRange = editor.editing.mapper.toViewRange(modelRange);
        const target = editor.editing.view.domConverter.viewRangeToDom(viewRange);
        return { target };
    }
    getMarkersAtSelection() {
        const { editor } = this;
        const { selection } = editor.model.document;
        const position = selection.getFirstPosition();
        if (!position)
            return [];
        const markers = Array.from(editor.model.markers.getMarkersAtPosition(position));
        const conditionMarkers = markers.filter((marker) => marker.name.startsWith('condition-block:') || marker.name.startsWith('condition-inline:'));
        // If there are nested conditions, return only the innermost one.
        // The innermost marker always has the latest start position in the document.
        // When two conditions share the same start (inner starts exactly at outer's edge),
        // break the tie by end position — the innermost ends earliest.
        if (conditionMarkers.length > 1) {
            return [
                conditionMarkers.reduce((innermost, current) => {
                    const cs = current.getRange().start;
                    const is = innermost.getRange().start;
                    if (cs.isAfter(is))
                        return current;
                    if (is.isAfter(cs))
                        return innermost;
                    return current.getRange().end.isBefore(innermost.getRange().end) ? current : innermost;
                }),
            ];
        }
        return conditionMarkers;
    }
}
